import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FilesService } from '../../../core/services/files.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { AgentService } from '../../../core/services/agent.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { AppUtils } from '../../../core/config/app.utils';
import { Observable } from 'rxjs';
import { IDeactivateComponent } from '../../../core/guards/deactivate.gaurd';

const AGENT_ADD_EDIT_ATTACHMENT = 'AGENT_ADD_EDIT_ATTACHMENT';

@Component({
  selector: 'app-add-edit-agent',
  templateUrl: './add-edit-agent.component.html',
  styleUrls: ['./add-edit-agent.component.scss'],
})
export class AddEditAgentComponent implements OnInit, IDeactivateComponent {
  breadcrumbs: any[] = [];
  status = 'create';
  agentForm: FormGroup | any;
  formErrors: any;
  agentId: any;
  attachments: any[] = [];
  prevPage = '';
  attachmentUploading = false;
  agentData: any;
  submit = false;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private filesService: FilesService,
    private router: Router,
    private route: ActivatedRoute,
    private agentService: AgentService,
    private message: NzMessageService,
  ) {
    this.formErrors = {
      name: {},
      contactNo: {},
      email: {},
      category: {},
    };
  }

  ngOnInit(): void {
    this.uploadLoader();
    this.initAgentForm();
    this.attachments = [];
    this.route.queryParams.subscribe((params) => {
      this.prevPage = params['prev'];
    });
    this.agentId = this.route.snapshot.paramMap.get('id');
    if (this.agentId) {
      this.loading = true;
      this.agentService.getAgentById(this.agentId).subscribe((res) => {
        this.status = 'edit';
        this.agentData = res;
        this.attachments = res.attachments;
        this.agentForm.patchValue(this.agentData);
        this.setBreadcrumbs();
        this.loading = false;
      });
    }
    this.setBreadcrumbs();
  }

  uploadLoader() {
    this.filesService.fileUploadingSubject.subscribe((res: any) => {
      switch (res.type) {
        case AGENT_ADD_EDIT_ATTACHMENT:
          this.attachmentUploading = res.loading;
          break;
      }
    });
  }

  initAgentForm(): void {
    this.agentForm = this.fb.group({
      category: [null, [Validators.required]],
      name: [
        null,
        [
          Validators.required,
          Validators.maxLength(70),
          Validators.pattern(/^[a-zA-Z0-9\s!@#$%^&*()_+=\-\\[\]{};':"\\|,.<>\/?]+$/),
        ],
      ],
      contactNo: [
        null,
        [
          Validators.required,
          Validators.maxLength(15),
          Validators.pattern(/^\d+$/),
        ],
      ],
      email: [
        null,
        [Validators.required, Validators.maxLength(100), Validators.email],
      ],
    });

    this.agentForm.valueChanges.subscribe((values: any) => {
      this.formErrors = AppUtils.getFormErrors(this.agentForm, this.formErrors);
    });
  }

  setBreadcrumbs(): void {
    if (this.status === 'edit') {
      this.breadcrumbs = [
        {
          title: ` Agents `,
          route: '/admin/agents',
          // icon: 'delivered-procedure',
        },
        {
          title: ` ${this.agentData?.refNo} `,
          // route: `/admin/agents/${data._id}`,
          // icon: 'delivered-procedure',
        },
        {
          title: ` Edit `,
        },
      ];
    } else {
      this.breadcrumbs = [
        {
          title: ` Agents `,
          route: '/admin/agents',
          // icon: 'delivered-procedure',
        },
        {
          title: ` Add `,
        },
      ];
    }
  }

  upload = (item: NzUploadXHRArgs) =>
    this.filesService
      .postFile(item.file, AGENT_ADD_EDIT_ATTACHMENT)
      .subscribe((res) => {
        this.message.create('success', `Attachment has been uploaded!`);
        const data = res as any;
        this.attachments.push({ name: item.file.name, url: data.url });
        this.filesService.fileUploadingSubject.next({
          type: AGENT_ADD_EDIT_ATTACHMENT,
          loading: false,
        });
      });

  onSubmit(): void {
    if (this.agentId) {
      this.agentService
        .updateAgent({
          _id: this.agentId,
          ...this.agentForm.value,
          attachments: this.attachments,
        })
        .subscribe(
          () => {
            this.message.create('success', `Agent has been updated!`);
            if (this.prevPage) {
              this.submit = true;
              this.router.navigate([`admin/agents/${this.agentId}`]);
            } else {
              this.submit = true;
              this.router.navigate(['admin/agents']);
            }
          },
          (e: any) => {
            if (e?.error?.errors?.email) {
              this.message.create('error', `Email is already existing!`);
            }
          },
        );
    } else {
      this.agentService
        .createAgent({
          ...this.agentForm.value,
          attachments: this.attachments,
        })
        .subscribe(
          () => {
            this.message.create('success', `New Agent has been added!`);
            if (this.prevPage === 'add-agent') {
              this.submit = true;
              this.router.navigate(['admin/agents/add-edit']);
            } else {
              this.submit = true;
              this.router.navigate(['admin/agents']);
            }
          },
          (e: any) => {
            if (e?.error?.errors?.email) {
              this.message.create('error', `Email is already existing!`);
            }
          },
        );
    }
  }

  cancel(): void {}

  confirm(): void {
    if (this.prevPage) {
      this.router.navigate([`admin/agents/${this.agentId}`]);
    } else {
      this.router.navigate(['admin/agents']);
    }
  }

  confirmRemoveAttachment(index: any): void {
    this.attachments.splice(index, 1);
    this.message.create('success', `Attachment has been deleted!`);
  }

  canExit(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.submit) {
      return true;
    } else if (this.agentForm.dirty || this.agentForm.touched) {
      return confirm(
        ' You have unsaved changes, Do you really want to discard these changes?',
      );
    } else {
      return true;
    }
  }
}
